<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterView, useRouter, useRoute } from 'vue-router'
import { useLocalStore } from './composables/useLocalStore'
import { useAuth } from './composables/useAuth'
import { useSync } from './composables/useSync'
import SyncStatus from './components/SyncStatus.vue'
import ThemeToggle from './components/ThemeToggle.vue'
import PaperGrain from './components/brand/PaperGrain.vue'
import FleckField from './components/brand/FleckField.vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const store = useLocalStore()
const { user, isAuthenticated, checkSession, logout } = useAuth()
const { sync, syncErrors } = useSync()
const router = useRouter()
const route = useRoute()
// Grid "tool" surfaces (editor, workspace) own the full viewport via ToolLayout,
// so the website header and its banners are suppressed for them.
const isToolRoute = computed(() => route.meta.tool === true)
// "Bare" routes (e.g. the design styleguide) render their own full-page chrome but
// still scroll normally — so they hide the website header/footer like tool routes,
// without the immersive non-scrolling viewport lock.
const hideChrome = computed(() => isToolRoute.value || route.meta.bare === true)
const initError = ref<string | null>(null)
// Blocked until initDB + seed complete so route components see a seeded DB on first mount.
const appReady = ref(false)

onMounted(async () => {
  // Run both in parallel; IndexedDB and session are independent
  await Promise.all([
    store
      .initDB()
      .then(() => store.seedLibraryIfNeeded())
      .catch((e: unknown) => {
        initError.value = e instanceof Error ? e.message : 'Failed to initialize database'
        console.error('Failed to initialize database:', e)
      }),
    checkSession(),
  ])
  appReady.value = true
})

async function handleLogout() {
  await logout()
  router.push('/login')
}

// Trigger a sync whenever the user logs in (transition false→true)
watch(isAuthenticated, (authed, wasAuthed) => {
  if (authed && !wasAuthed) void sync()
})
</script>

<template>
  <!--
    Tool routes (editor, workspace) are immersive: ToolLayout fills the dynamic
    viewport (100dvh) and must not scroll. The shell therefore tracks the *dynamic*
    viewport too — `min-h-screen` is 100vh (the iOS *large* viewport, address bar
    retracted), which is taller than the visible area while the address bar shows,
    leaving a scrollable gap of empty space below the 100dvh tool shell (#207). On
    website routes the shell stays `min-h-screen` so ordinary pages scroll normally.
  -->
  <div
    :class="['flex flex-col', isToolRoute ? 'h-dvh overflow-hidden' : 'min-h-screen']"
    data-testid="app-shell"
  >
    <!-- Material layer (#241): a fixed paper-grain veil over the whole app. Inert
         and behind interaction; warms every flat surface app-wide. -->
    <PaperGrain />

    <!--
      Brand banner (#244): the app header wears the styleguide's banner treatment —
      dark in BOTH themes. Light mode lifts it only a quarter-notch ("dawn" #140F0A
      over the same near-black "night" #100C08, both held in --header-bg), so the
      toggle reads like a sky changing, never a light↔dark inversion. The gold
      wordmark (--brand-gold, fixed across themes) and the red+gold FleckField specks
      ride on top. Because the bar is dark in both themes, nav ink is the on-banner
      --tag tone (AA on --header-bg both ways) rather than the page's --foreground.
    -->
    <header
      v-if="!hideChrome"
      class="relative overflow-hidden bg-[var(--header-bg)] shadow-xs"
      data-testid="site-header"
    >
      <!-- Decorative gilt/madder flecks behind the bar content. -->
      <FleckField class="pointer-events-none absolute inset-0" />
      <div class="relative max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between gap-3">
          <router-link
            to="/"
            class="flex items-center gap-2 min-w-0"
            aria-label="Cross Stitch Club — home"
          >
            <!-- "Cross My Heart" ♥ motif (#193): U+2665 + U+FE0E forces the
                 monochrome text glyph. Gilt on the dark banner (the rose accent
                 fails AA on near-black in Daylight), matching the wordmark. -->
            <span class="text-2xl text-[var(--brand-gold)]" aria-hidden="true"
              >&#x2665;&#xFE0E;</span
            >
            <!-- Gold wordmark (#244): --brand-gold, Cormorant display small-caps.
                 Hidden on the smallest phones — the no-overflow header contract
                 (#103/#109) has no room for it beside the nav at 390px, so the gilt
                 ♥ mark carries the brand there; the full wordmark returns at sm+. -->
            <span
              class="hidden truncate font-display text-lg font-medium uppercase leading-none tracking-[0.2em] text-[var(--brand-gold)] sm:block"
            >
              Cross Stitch Club
            </span>
          </router-link>
          <!--
            Account-level controls (profile, theme, sign out) live in an overflow menu so the
            bar fits a phone (#103). Primary nav (My Patterns, Import) stays visible.
          -->
          <nav class="flex items-center gap-2 sm:gap-3">
            <template v-if="isAuthenticated">
              <SyncStatus />
              <router-link
                to="/patterns"
                class="text-sm text-[var(--tag)] hover:text-[var(--brand-gold)] transition-colors whitespace-nowrap"
              >
                My Patterns
              </router-link>
              <router-link
                to="/patterns/import"
                class="bg-primary text-primary-foreground px-3 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                Import
              </router-link>
              <DropdownMenu>
                <DropdownMenuTrigger
                  class="inline-flex items-center justify-center rounded-lg border border-[var(--tag)]/30 p-2 text-[var(--tag)] hover:text-[var(--brand-gold)] hover:bg-white/10 transition-colors"
                  aria-label="Account menu"
                  data-testid="account-menu-trigger"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                  <DropdownMenuLabel class="truncate">
                    {{ user?.name || user?.email }}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem as-child>
                    <router-link to="/profile" class="w-full cursor-pointer">Account</router-link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div class="px-2 py-1.5">
                    <p class="mb-1.5 text-xs font-medium text-muted-foreground">Theme</p>
                    <ThemeToggle />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    class="cursor-pointer"
                    data-testid="sign-out"
                    @select="handleLogout"
                  >
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </template>
            <template v-else>
              <ThemeToggle />
              <router-link
                to="/login"
                class="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                Sign in
              </router-link>
            </template>
          </nav>
        </div>
      </div>
    </header>

    <!-- Database initialization error -->
    <div
      v-if="initError && !hideChrome"
      class="bg-destructive/10 border-b border-destructive/30 px-4 py-3 text-center text-destructive"
    >
      <strong>Database Error:</strong> {{ initError }}
      <p class="text-sm mt-1">Try refreshing the page or clearing your browser's IndexedDB data.</p>
    </div>

    <!-- Sync errors -->
    <div
      v-if="syncErrors.length && !hideChrome"
      class="bg-warning/10 border-b border-warning/30 px-4 py-3 text-warning"
      data-testid="sync-error-banner"
    >
      <strong>Sync errors</strong> — some patterns could not be saved to the server:
      <ul class="mt-1 list-disc list-inside text-sm">
        <li v-for="err in syncErrors" :key="err">{{ err }}</li>
      </ul>
    </div>

    <main class="flex-1">
      <RouterView v-if="appReady" />
    </main>

    <!-- Footer: website chrome only (hidden on immersive tool routes). -->
    <footer
      v-if="!hideChrome"
      class="mt-auto border-t border-border px-4 py-6 text-center text-sm text-muted-foreground"
      data-testid="site-footer"
    >
      <nav class="flex items-center justify-center gap-4">
        <router-link to="/privacy" class="hover:text-foreground transition-colors"
          >Privacy</router-link
        >
        <span aria-hidden="true">·</span>
        <router-link to="/terms" class="hover:text-foreground transition-colors">Terms</router-link>
      </nav>
    </footer>
  </div>
</template>
